import {
  Box,
  Button,
  IconButton,
  Skeleton,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  FormControlLabel,
  Switch,
  Fab
} from "@mui/material";
import { Navigate, useLocation, useParams, useSearchParams } from "react-router-dom";
import Fireworks from "react-canvas-confetti/dist/presets/fireworks";
import axios from "axios";
import { useEffect, useMemo, useRef, useState } from "react";
import { Global, css } from "@emotion/react";

import { StatusCategory, FlipChecklistTaskInfo, EventType } from "../types";
import GenerateTasksModal from "../GenerateTasksModal";
import Iconify from "../../../minimals/components/iconify";
import { useOrderInfo } from "../../shared/orderInfo/OrderInfoContext";
import AlertSyncingWithSelectRefresh from "../AlertSyncingWithSelectRefresh";
import { isBuyerOrBorrower, isLender, isMTA, isSeller } from "../filterConditions";
import { Contact } from "../../shared/orderInfo/types";
import Page from "../../../minimals/components/page";
import NewTasksTable from "./NewTasksTable";
import { Category, NewTask, NewTaskGroupEntity } from "./types";
import NewTaskHistory from "./NewTaskHistory";
import NewAddTaskModal from "./NewAddTaskModal";
import NewTaskOrderInfo from "../orderInfo/NewTaskOrderInfo";
import { TConductorInstance } from "react-canvas-confetti/dist/types";
import { PageWrap, ListWrap, TaskActionStack, NoneFoundBox, NoneFoundIllus } from "../orderInfo/styles";
import OrderNotesModal from "./notes/OrderNotesModal";
import UploadModal from "../../shared/UploadModal";
import NewGroupEntityDocumentsModal from "../orderInfo/NewGroupEntityDocumentsModal";
import { ChecklistQaProvider, useChecklistQa } from "../qa/ChecklistQaContext";
import ChecklistQaToolbar from "../qa/ChecklistQaToolbar";
import { StyledBadge } from "../../shared/StyledBadge";

const applications = [
  { id: 1, name: "flips", friendlyName: "Flip" },
  { id: 2, name: "closingchecklist", friendlyName: "Closing Checklist" }
];

const excludedContactTypes = [
  "Abstractor",
  "Appraiser",
  "GeneralContractor",
  "Government",
  "HOA",
  "HomeInspector",
  "Surveyor",
  "Underwriter",
  "SettlementAgent"
];

const NewTasks = () => {
  const qa = useChecklistQa();
  const orderInfo = useOrderInfo();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const orderNumber = searchParams.get("order");
  const skipRedirect = searchParams.get("skip-redirect");
  const { taskType } = useParams();
  const applicationType = taskType || applications.find((application) => location.pathname.endsWith(`/${application.name}`))?.name;
  let applicationTypeFriendlyName = applications.find((a) => a.name === applicationType)?.friendlyName || "";
  const [statusCategories, setStatusCategories] = useState<StatusCategory[]>([]);
  const [eventTypes, setEventTypes] = useState<EventType[]>([]);
  const [tasks, setTasks] = useState<NewTask[] | null>(null);
  const [initiateIsPending, setInitiateIsPending] = useState<boolean>(false);
  const [flipChecklistTaskInfo, setFlipChecklistTaskInfo] = useState<FlipChecklistTaskInfo | null>(null);
  const [taskGroupEntity, setTaskGroupEntity] = useState<NewTaskGroupEntity | null>(null);
  const [taskGroupEntityLoading, setTaskGroupEntityLoading] = useState<boolean>(true);
  const applicationId = applications.find((a) => a.name === applicationType)?.id || 1;
  const [selectedTaskHistory, setSelectedTaskHistory] = useState<NewTask | null>(null);
  const [addTaskMode, setAddTaskMode] = useState<boolean>(false);
  const [alertSyncingWithSelectRefresh, setAlertSyncingWithSelectRefresh] = useState<boolean>(false);
  const [globalFilter, setGlobalFilter] = useState("All");
  const [categories, setCategories] = useState<Category[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<number | null>(0);
  const [hasOldTasks, setHasOldTasks] = useState<boolean | null>(null);
  const [showedConfetti, setShowedShowConfetti] = useState<boolean>(false);
  const [addOrderNoteMode, setAddOrderNoteMode] = useState<boolean>(false);
  const [isAddDocumentModalOpen, setIsAddDocumentModalOpen] = useState<boolean>(false);
  const [isViewEntityDocumentsModalOpen, setIsViewEntityDocumentsModalOpen] = useState<boolean>(false);
  const [isCondensed, setIsCondensed] = useState<boolean>(false);
  const [hideCompleted, setHideCompleted] = useState<boolean>(false);
  const categoriesWithNone = [
    ...(categories ?? []), 
    { id: -1, name: "None" },
  ];
  const confettiController = useRef<TConductorInstance>();

  const onInitConfetti = ({ conductor }: any) => {
    confettiController.current = conductor;
  };

  const runConfetti = () => {
    if (!confettiController.current) return;
    confettiController.current.run({ speed: 3 });
    setTimeout(() => {
      confettiController?.current?.pause();
    }, 3000);
  };

  const contacts = orderInfo?.contacts.filter((c) => c.name && !excludedContactTypes.includes(c.type)) || [];

  const initiateTasks = async () => {
    setInitiateIsPending(true);

    if (applicationType === "flips") {
      // await axios.post("/api/taskmanagement/flips/initiate", { orderNumber });
      // await getAndSetFlipChecklistTask();
    } else if (applicationType === "closingchecklist") {
      await axios.post("/api/taskmanagement/newtasks/initiateclosingchecklist", { orderNumber });
    }

    await fetchTasks();
    await getAndSetTaskGroupEntity();
    setInitiateIsPending(false);
  };

  const handleShowConfetti = (tasks: NewTask[]) => {
    var isAllTasksComplete =
      tasks.length > 0 &&
      tasks.every((t) => t.currentOmitOption.statusApplicableTo === 3 || t.currentOmitOption.statusApplicableTo === 4);
    if (isAllTasksComplete && !showedConfetti) {
      runConfetti();
      setShowedShowConfetti(true);
    } else {
      setShowedShowConfetti(false);
    }
  };

  const fetchTasks = async () => {
    const { data } = await axios.get<NewTask[]>(
      `/api/taskmanagement/newtasks/GetForOrderNumber?orderNumber=${orderNumber}&applicationid=2`
    );
    setTasks(data);
    handleShowConfetti(data);
    return data;
  };

  const getStatusCategories = async () => {
    const { data } = await axios.get(`/api/taskmanagement/tasks/GetStatusCategories`);
    setStatusCategories(data);
  };

  const getAndSetCategories = async () => {
    const { data } = await axios.get("/api/taskmanagement/newtasks/getCategories");
    setCategories(data);
  };

  const getEventTypes = async () => {
    const { data } = await axios.get(`/api/taskmanagement/tasks/getEventTypes`);
    setEventTypes(data);
  };

  const getAndSetFlipChecklistTask = async () => {
    const { data } = await axios.get(`/api/taskmanagement/flips/GetFlipChecklistTask?ordernumber=${orderNumber}`);
    setFlipChecklistTaskInfo(data);
  };

  const getAndSetTaskGroupEntity = async () => {
    setTaskGroupEntityLoading(true);
    const { data } = await axios.get(`/api/taskmanagement/newTaskGroupEntity/getfororder?ordernumber=${orderNumber}`);
    setTaskGroupEntity(data);
    setTaskGroupEntityLoading(false);
  };

  const syncChecklistWithSelect = async () => {
    const { data } = await axios.post(`/api/taskmanagement/newtasks/SyncWithSelect`, { orderNumber });
    if (data.hadChanges) {
      setAlertSyncingWithSelectRefresh(true);
    }
  };

  const handleDocumentsSave = async (documentIds: number[]) => {
    if (!orderNumber || !documentIds?.length) return;
    await axios.post("/api/taskmanagement/newTaskGroupEntity/addMomentumDocuments", {
      momentumDocumentIds: documentIds,
      orderNumber
    });
    await getAndSetTaskGroupEntity();
  };

  useEffect(() => {
    const loadData = async () => {
      const [, , loadedTasks] = await Promise.all([
        getStatusCategories(), getEventTypes(), fetchTasks(), getAndSetTaskGroupEntity()
      ]);

      if (applicationType === "flips") {
        await getAndSetFlipChecklistTask();
      } else if (applicationType === "closingchecklist") {
        await Promise.all([
          loadedTasks.length > 0 ? syncChecklistWithSelect() : Promise.resolve(),
          getAndSetCategories()
        ]);
      }
    };

    loadData();
  }, []);

  useEffect(() => {
    if (skipRedirect) {
      setHasOldTasks(false);
      return;
    }
    (async () => {
      if (applicationType === "closingchecklist" && tasks && tasks.length === 0) {
        const { data } = await axios.get(`/api/taskmanagement/tasks/HasOldChecklistTasks?ordernumber=${orderNumber}`);
        setHasOldTasks(data.hasAny);
        if (data.hasAny) {
          window.location.href = `/task-managementv1/closingchecklist?order=${orderNumber}`;
        }
      }
    })();
  }, [tasks]);

  if (!applicationType) {
    return <Navigate to="/orders" />;
  }

  const filteredTasks = useMemo(() => {
    if (applicationType !== "closingchecklist") {
      return tasks;
    }

    return tasks
    ?.filter((t) => !qa.qaOnly || qa.state.notes.some((note) => note.taskId === t.id))
    .filter((t) => {
      if (selectedCategory === -1) {
        return t.categoryId == null;
      }
      return selectedCategory ? t.categoryId === selectedCategory : true;
    })
      .filter((t) => {
        const contact = contacts.find((c) => c.code === t.contactCode) as Contact;
        switch (globalFilter) {
          case "Buyer":
          case "Borrower":
            return isBuyerOrBorrower(t, contact);
          case "Seller":
            return isSeller(t, contact);
          case "Lender":
            return isLender(t, contact);
          case "MTA":
            return isMTA(t);
          default:
            return true;
        }
      });
  }, [applicationType, globalFilter, tasks, contacts, selectedCategory, qa.qaOnly, qa.state.notes]);

  const activeFilterLabels: React.ReactNode[] = [];

  if (globalFilter && globalFilter !== "All") {
    activeFilterLabels.push(
      <span key="globalFilter" className="filter-label">
        {globalFilter}
      </span>
    );
  }

  if (selectedCategory && selectedCategory !== 0) {
    const categoryName = categories.find((c) => c.id === selectedCategory)?.name;
    if (categoryName) {
      activeFilterLabels.push(
        <span key="selectedCategory" className="filter-label">
          {categoryName}
        </span>
      );
    }
  }

  const noneFoundLabel =
    activeFilterLabels.length > 0 ? (
      <div className="kkdd">
        No Tasks Yet for{" "}
        {activeFilterLabels.map((label, idx) => (
          <span key={idx}>
            {label}
            {idx < activeFilterLabels.length - 1 && " + "}
          </span>
        ))}
      </div>
    ) : (
      "None found"
    );

  return (
    <Page title={orderNumber || ""}>
      <Global
        styles={css`
          main {
            padding-top: 64px !important;
            padding-bottom: 0px !important;
            padding-left: ${isCondensed ? "0px" : "16px"} !important;
            padding-right: ${isCondensed ? "0px" : "16px"} !important;
            /* min-height: 100vh !important; */
          }
        `}
      />
      <PageWrap isCondensed={isCondensed}>
        <Stack
          direction={isCondensed ? "row" : "column"}
          justifyContent="flex-start"
          sx={{
            "@media (max-width: 1199px)": {
              marginTop: "0px"
            },
            height: isCondensed ? "calc(100vh - 64px)" : "fit-content",
            marginTop: isCondensed ? 0 : "24px"
          }}
        >
          {applicationType === "flips" && flipChecklistTaskInfo && (
            <Stack direction="row" justifyContent="right" spacing={3} alignItems="center" marginBottom={1}>
              {flipChecklistTaskInfo?.assignedTo && (
                <Typography variant="subtitle2">
                  Marked as Flip by: <Typography display="inline-block">{flipChecklistTaskInfo?.assignedTo}</Typography>
                </Typography>
              )}
              {flipChecklistTaskInfo?.completedBy && (
                <Typography variant="subtitle2">
                  Marked{" "}
                  <Box
                    sx={{ color: flipChecklistTaskInfo.status === "Completed" ? "success.main" : "error.main" }}
                    display="inline-block"
                  >
                    {flipChecklistTaskInfo.status === "Completed" ? "Approved" : "Rejected"}
                  </Box>{" "}
                  by:{" "}
                  <Typography color="#000" display="inline-block">
                    {flipChecklistTaskInfo?.completedBy}
                  </Typography>
                </Typography>
              )}
            </Stack>
          )}

          <Stack
            direction={isCondensed ? "column" : "row"}
            alignItems="flex-start"
            justifyContent={isCondensed ? "flex-start" : "space-between"}
            spacing={2}
            sx={{ width: "100%", overflow: "auto" }}
          >
            <NewTaskOrderInfo orderInfo={orderInfo} isCondensed={isCondensed} />
            <TaskActionStack direction={isCondensed ? "row" : "column"} spacing={1} isCondensed={isCondensed}>
              <Stack direction="row" spacing={1} alignItems="center" justifyContent="center" flexWrap="wrap">
                <Button
                  variant="contained"
                  onClick={() => setAddTaskMode(true)}
                  startIcon={<Iconify icon="eva:plus-fill" />}
                  size="small"
                >
                  Add Task
                </Button>
                <Tooltip title="Change Layout">
                  <Fab
                    onClick={() => setIsCondensed(!isCondensed)}
                    size="small"
                    color="primary"
                    sx={{
                      position: "fixed",
                      bottom: 16,
                      right: 16,
                      zIndex: 1000
                    }}
                  >
                    <Iconify icon={isCondensed ? "eva:expand-outline" : "eva:collapse-outline"} />
                  </Fab>
                </Tooltip>
              </Stack>
              <Stack
                direction="row"
                spacing={isCondensed ? 0 : 1}
                alignItems="center"
                justifyContent="center"
                flexWrap="wrap"
                className="action_icons-wrap"
                sx={{
                  flexDirection: {
                    md: isCondensed ? "row" : "column",
                    lg: isCondensed ? "row" : "row"
                  }
                }}
              >
                {!taskGroupEntityLoading && (
                  <>
                    <Tooltip title="View Documents">
                      <IconButton onClick={() => setIsViewEntityDocumentsModalOpen(true)}>
                        <StyledBadge badgeContent={taskGroupEntity?.documents?.length} color="info">
                          <Iconify icon="iconamoon:folder-document" />
                        </StyledBadge>
                      </IconButton>
                    </Tooltip>

                    <Tooltip title="Upload Documents">
                      <IconButton onClick={() => setIsAddDocumentModalOpen(true)}>
                        <Iconify icon="eva:upload-fill" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Add Notes">
                      <IconButton onClick={() => setAddOrderNoteMode(true)}>
                        <StyledBadge badgeContent={taskGroupEntity?.notes?.length} color="error">
                          <Iconify icon="eva:file-add-outline" />
                        </StyledBadge>
                      </IconButton>
                    </Tooltip>
                  </>
                )}
                {taskGroupEntityLoading && <Skeleton variant="circular" width={36} height={36} />}
              </Stack>
            </TaskActionStack>
          </Stack>
        </Stack>
        <ListWrap isCondensed={isCondensed}>
          {applicationType === "closingchecklist" && <ChecklistQaToolbar />}
          <Stack
            spacing={{ xs: 2, lg: isCondensed ? 2 : 4 }}
            marginBottom={0}
            direction="row"
            justifyContent="flex-start"
            alignItems={{ xs: "flex-end", lg: "center" }}
            sx={{
              mt: isCondensed ? 0 : 2,
              ".MuiToggleButton-root": {
                whiteSpace: "nowrap",
                fontSize: { sm: "0.7rem", lg: "0.8rem" },
                margin: { xs: 0.5, lg: 0.5 },
                padding: { xs: "2px 4px", lg: "2px 8px" }
              }
            }}
          >
            <Stack
              direction={{ xs: "column", lg: "row" }}
              alignItems={{ xs: "flex-start", lg: "center" }}
              spacing={0.75}
              flexWrap="wrap"
              gap={0.5}
            >
              <Typography fontWeight={600} sx={{ fontSize: { sm: "0.7rem", lg: "0.875rem" } }}>
                Responsible Party
              </Typography>
              <ToggleButtonGroup
                value={globalFilter}
                color="primary"
                size="small"
                exclusive
                onChange={(e, value) => setGlobalFilter(value)}
                disabled={!orderInfo}
              >
                <ToggleButton value="All">All</ToggleButton>
                <ToggleButton value="MTA">MTA</ToggleButton>
                {!orderInfo ? (
                  <Stack sx={{ display: "flex", flexDirection: "row", gap: 1, alignItems: "center", pr: 1 }}>
                    <Skeleton variant="rounded" width={80} height={28} />
                    <Skeleton variant="rounded" width={80} height={28} />
                    <Skeleton variant="rounded" width={80} height={28} />
                  </Stack>
                ) : (
                  <>
                    {orderInfo?.transactionType === "Refinance" && (
                      <ToggleButton value="Borrower">Borrower</ToggleButton>
                    )}
                    {orderInfo?.transactionType === "Purchase" && <ToggleButton value="Buyer">Buyer</ToggleButton>}
                    {orderInfo?.loanAmounts && orderInfo?.loanAmounts.length > 0 && (
                      <ToggleButton value="Lender">Lender </ToggleButton>
                    )}
                    {orderInfo?.transactionType === "Purchase" && <ToggleButton value="Seller">Seller</ToggleButton>}
                  </>
                )}
              </ToggleButtonGroup>
            </Stack>

            <Stack
              direction={{ xs: "column", lg: "row" }}
              alignItems={{ xs: "flex-start", lg: "center" }}
              spacing={0.75}
              flexWrap="wrap"
              gap={0.5}
            >
              <Typography fontWeight={600} sx={{ fontSize: { sm: "0.7rem", lg: "0.875rem" } }}>
                Category
              </Typography>
              <ToggleButtonGroup
                value={selectedCategory}
                color="primary"
                size="small"
                exclusive
                onChange={(e, value) => setSelectedCategory(value)}
                disabled={!orderInfo || categoriesWithNone.length === 0}
              >
                <ToggleButton value={0}>All</ToggleButton>
                {categoriesWithNone.length === 0 ? (
                  <Stack sx={{ display: "flex", flexDirection: "row", gap: 1, alignItems: "center", pr: 1 }}>
                    <Skeleton variant="rounded" width={120} height={28} />
                    <Skeleton variant="rounded" width={100} height={28} />
                    <Skeleton variant="rounded" width={90} height={28} />
                    <Skeleton variant="rounded" width={70} height={28} />
                  </Stack>
                ) : (
                  categoriesWithNone.map((c) => (
                    <ToggleButton key={c.id} value={c.id}>
                      {c.name}
                    </ToggleButton>
                  ))
                )}
              </ToggleButtonGroup>
            </Stack>
            <Stack
              direction={isCondensed ? "column" : "row"}
              alignItems={isCondensed ? "flex-start" : "center"}
              spacing={0.75}
            >
              <FormControlLabel
                control={
                  <Switch checked={hideCompleted} onChange={(_, value) => setHideCompleted(value)} size="small" />
                }
                label="Hide Completed"
                sx={{
                  ".MuiTypography-root": {
                    whiteSpace: "nowrap",
                    fontWeight: 600,
                    fontSize: { sm: "0.75rem", md: "0.75rem", lg: "0.875rem" }
                  }
                }}
              />
            </Stack>
          </Stack>
          {filteredTasks && filteredTasks.length > 0 && (
            <NewTasksTable
              tasks={filteredTasks}
              setTasks={setTasks}
              statusCategories={statusCategories}
              eventTypes={eventTypes}
              refreshTasks={async () => { await fetchTasks(); }}
              orderNumber={orderNumber}
              setSelectedTaskHistory={setSelectedTaskHistory}
              contacts={contacts}
              categories={categories}
              isCondensed={isCondensed}
              hideCompleted={hideCompleted} // Add this
              setHideCompleted={setHideCompleted}
            />
          )}

          {filteredTasks && filteredTasks.length === 0 && (
            <NoneFoundBox>
              <div>
                <NoneFoundIllus />
                {noneFoundLabel}
              </div>
            </NoneFoundBox>
          )}
          {tasks && tasks.length === 0 && hasOldTasks === false && (
            <GenerateTasksModal
              initiateTasks={initiateTasks}
              initiateIsPending={initiateIsPending}
              applicationType={applicationType}
              applicationTypeFriendlyName={applicationTypeFriendlyName}
              orderNumber={orderNumber}
            />
          )}
          {selectedTaskHistory && (
            <NewTaskHistory
              task={selectedTaskHistory}
              eventTypes={eventTypes}
              onClose={() => setSelectedTaskHistory(null)}
            />
          )}
          {addTaskMode && (
            <NewAddTaskModal
              open={addTaskMode}
              closeModal={() => setAddTaskMode(false)}
              applicationId={applicationId}
              orderNumber={orderNumber}
              refreshTasks={async () => { await fetchTasks(); }}
              tasks={tasks || []}
            />
          )}
          {addOrderNoteMode && (
            <OrderNotesModal
              taskGroupEntity={taskGroupEntity}
              orderNumber={orderNumber || ""}
              closeModal={() => setAddOrderNoteMode(false)}
              refresh={getAndSetTaskGroupEntity}
            />
          )}
          {isAddDocumentModalOpen && (
            <UploadModal
              open={isAddDocumentModalOpen}
              handleClose={() => setIsAddDocumentModalOpen(false)}
              updateItems={() => {}}
              onSave={handleDocumentsSave}
              documentInfo={{} as any}
              orderNumber={orderNumber || ""}
              hasDocumentType
              hasAdditionalDescription
              isUseNameOfFile
              applicationInitiator="TitleQ.TaskManagement"
            />
          )}
          {isViewEntityDocumentsModalOpen && (
            <NewGroupEntityDocumentsModal
              orderNumber={orderNumber || ""}
              handleClose={() => setIsViewEntityDocumentsModalOpen(false)}
            />
          )}
          {alertSyncingWithSelectRefresh && <AlertSyncingWithSelectRefresh open={alertSyncingWithSelectRefresh} />}
          <Fireworks onInit={onInitConfetti} />
        </ListWrap>
      </PageWrap>
    </Page>
  );
};

export default function NewTasksWithQa() {
  return <ChecklistQaProvider><NewTasks /></ChecklistQaProvider>;
}
